import { CommonModule } from '@angular/common'
import { Component, Input, OnDestroy, OnInit, TemplateRef } from '@angular/core'
import { FormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms'
import { TagsComponent } from '@app/modules/@micetribe/events/tags/components/tags/tags.component'
import { ToastrService } from '@app/services/toastr.service'
import { NbButtonModule, NbCardModule, NbDialogService, NbFormFieldComponent, NbInputModule, NbToggleModule } from '@nebular/theme'
import { LanguageService } from '@personas/services/language.service'
import { Subject } from 'rxjs'
import { distinctUntilChanged, takeUntil } from 'rxjs/operators'
import { PersonaFieldTypes } from '../../enums/persona-fields-types.enum'
import { PersonaFieldValue } from '../../interfaces/persona-field-value.interface'
import { PersonaField } from '../../interfaces/persona-field.interface'
import { Language } from '../../interfaces/persona-options.interface'
import { PersonaFactoryService } from '../../models/persona.model'

@Component({
  selector: 'ngx-form-languages',
  templateUrl: './form-languages.component.html',
  styleUrls: ['./form-languages.component.scss'],
  standalone: true,
  imports: [CommonModule, NbCardModule, NbButtonModule, FormsModule, TagsComponent, NbInputModule, NbToggleModule]
})
export class FormLanguagesComponent implements OnInit, OnDestroy {
  /** List of available languages */
  private _availableLanguages: Language[]
  /** New language to be added */
  newLanguage: Language
  /** Language to edit translations for */
  editTranslationFor: Language
  /** Form group for the persona */
  @Input()
  form: UntypedFormGroup

  //using temp form at the html file to avoid auto unfocusing input when change
  @Input()
  tempform: UntypedFormGroup
  /** Temporary variable to store the translations for the selected persona */
  formTranslations: UntypedFormGroup
  /** Field types enum to be used in template */
  PersonaFieldTypes = PersonaFieldTypes
  /** Subject that stops all hot observables */
  private _destroy$ = new Subject()
  formLanguages: string[] = []
  translationValue: InputEvent
  elementToUpdateIndex: number
  valueToUpdateIndex: number
  constructor(
    private personaFactoryService: PersonaFactoryService,
    private languageService: LanguageService,
    private dialogService: NbDialogService,
    private toastrService: ToastrService
  ) {}

  /**
   * Initialize the component and get the available languages
   * @returns void
   */
  ngOnInit() {
    this.newLanguage = { slug: '', name: '', active: true, right_to_left: false }

    // Initialize tempform once
    this.tempform = this.personaFactoryService.generateFormGroupForm(this.form)

    this.languageService
      .getLanguages()
      .pipe(takeUntil(this._destroy$))
      .subscribe((languages: Language[]) => {
        this._availableLanguages = languages
        this._availableLanguages.filter((language) => this.form.value.options.languages.includes(language.slug)).forEach((language) => (language.active = true))
        this._availableLanguages.filter((language) => !language.active).forEach((language) => (language.active = false))
        this.formLanguages = this.form.value.options.languages
      })

    // Subscribe to form changes with distinctUntilChanged
    this.form.controls['form_elements'].valueChanges
      .pipe(
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        takeUntil(this._destroy$)
      )
      .subscribe(() => {
        this.tempform.patchValue(
          {
            form_elements: this.form.get('form_elements').value
          },
          { emitEvent: false }
        )
      })

    this.languageService.addNewFormFieldEvent$.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.updateCurrentLanguages(this.formLanguages)
    })
  }

  /**
   * Get the available languages to select from
   * @returns Language[]
   */
  availableLanguages() {
    return this._availableLanguages
  }

  /**
   * Unsubscribe from all subscriptions
   * @returns void
   */
  ngOnDestroy(): void {
    this._destroy$.next(null)
    this._destroy$.complete()
  }

  /**
   * Handle the language change
   * @param languageSlugsList string[]
   */
  updateCurrentLanguages(languageSlugsList: string[]): void {
    this.formLanguages = languageSlugsList
    this.form.controls.options.get('languages').patchValue(languageSlugsList)
    this.form.controls.form_elements['controls'].forEach((element: UntypedFormGroup) => {
      this.removeUnavailableTranslations(languageSlugsList, element)
      this.fillDefaultTranslations(element, languageSlugsList)
    })
  }

  /**
   * Remove the translations for the unavailable languages
   * @param languageSlugsList
   * @param element
   */
  private removeUnavailableTranslations(languageSlugsList: string[], element: UntypedFormGroup) {
    const elementTranslations: UntypedFormGroup = element.controls.options['controls'].translations
    const elementValues: UntypedFormGroup[] = element.controls.form_element_values['controls']
    Object.keys(elementTranslations.controls).forEach((languageSlug) => {
      if (!languageSlugsList.includes(languageSlug)) {
        elementTranslations.removeControl(languageSlug)
      }
    })
    elementValues.forEach((value) => {
      const valueTranslations: UntypedFormGroup = value.controls.options['controls'].translations
      Object.keys(valueTranslations.controls).forEach((languageSlug) => {
        if (!languageSlugsList.includes(languageSlug)) {
          value.controls.options['controls'].translations.removeControl(languageSlug)
        }
      })
    })
  }

  /**
   * Opens a default dialog
   * @param dialog TemplateRef<any>
   * @param data any
   */
  openDialog(dialog: TemplateRef<any>, data: any = null) {
    this.newLanguage = { slug: '', name: '', active: true, right_to_left: false }
    this.dialogService.open(dialog, {
      closeOnBackdropClick: false,
      closeOnEsc: false,
      autoFocus: false,
      context: data
    })
  }

  /**
   * Open the edit language translations dialog
   * @param dialog TemplateRef<any>
   * @param language Language
   */
  openEditTranslationDialog(dialog: TemplateRef<any>, language: Language) {
    this.editTranslationFor = language
    this.formTranslations = this.personaFactoryService.generateFormGroupForm(this.form)
    this.dialogService
      .open(dialog, {
        closeOnBackdropClick: false,
        closeOnEsc: false
      })
      .onClose.pipe(takeUntil(this._destroy$))
      .subscribe((o) => {
        this.editTranslationFor = { name: '', slug: '' }
        this.formTranslations = new UntypedFormGroup({})
      })
  }

  /**
   * Fill the default translations for the selected language if they don't exist
   * @param element FormGroup
   * @param languageSlugList string[]
   */
  private fillDefaultTranslations(element: UntypedFormGroup, languageSlugList: string[]) {
    languageSlugList.forEach((languageSlug) => {
      if (!Object.prototype.hasOwnProperty.call(element.value.options.translations, languageSlug)) {
        const translations: UntypedFormGroup = element.controls.options['controls'].translations
        translations.addControl(languageSlug, new UntypedFormControl(this.languageService.loadDefaultTranslation(element.value.form_element_type_id, languageSlug)))
        if (element.value.form_element_values.length > 0) {
          element.value.form_element_values.forEach((value: PersonaFieldValue) => {
            if (!Object.prototype.hasOwnProperty.call(value.options.translations, languageSlug)) {
              value.options.translations[languageSlug] = this.languageService.loadDefaultValueTranslations(value.slug, languageSlug) || value.name
            }
          })
        }
      }
    })
    this.form.controls.form_elements['controls'].find((el) => el.value.name === element.value.name).patchValue(element)
  }

  /**
   * Create a new language in the workspace options and add it to the list of available languages
   */
  addLanguage() {
    this.languageService
      .createLanguage({ name: this.newLanguage.name, right_to_left: this.newLanguage.right_to_left })
      .pipe(takeUntil(this._destroy$))
      .subscribe(([workspace, lang]) => {
        this.toastrService.success(`${this.newLanguage.name} was created successfully`, '')
        this.newLanguage.slug = lang.slug
        this._availableLanguages.push(this.newLanguage)
        this.updateCurrentLanguages([...this.form.value.options.languages, this.newLanguage.slug])
      })
  }

  /**
   * Handle the form element name translation change
   * @param event
   * @param formElement
   */
  onFieldNameChange(event: InputEvent, formElement: PersonaField) {
    const elementToUpdate: UntypedFormGroup = this.formTranslations.controls.form_elements['controls'].find((element: UntypedFormGroup) => element.value.name === formElement.name)
    elementToUpdate.controls.options['controls'].translations['controls'][this.editTranslationFor.slug].patchValue(event)
  }

  /**
   * Handle the form element value translation change
   * @param event
   * @param formElement
   * @param valueToTranslate
   */
  onFieldValueChange(event: InputEvent, formElement: PersonaField, valueToTranslate: PersonaFieldValue) {
    const elementToUpdate = this.formTranslations.controls.form_elements['controls'].find(
      (element: UntypedFormGroup) => element.value.name.toLowerCase() === formElement.name.toLowerCase()
    )

    this.elementToUpdateIndex = this.formTranslations.controls.form_elements['controls'].findIndex(
      (element: UntypedFormGroup) => element.value.name.toLowerCase() === formElement.name.toLowerCase()
    )
    this.valueToUpdateIndex = elementToUpdate.controls.form_element_values['controls'].findIndex(
      (value: UntypedFormGroup) => value.value.name.toLowerCase() === valueToTranslate.name.toLowerCase()
    )
    const formTranslationsValue = this.formTranslations.value
    formTranslationsValue['form_elements'][this.elementToUpdateIndex]['form_element_values'][this.valueToUpdateIndex]['options']['translations'][this.editTranslationFor.slug] =
      event
    this.formTranslations.patchValue(formTranslationsValue)
    this.translationValue = event
  }

  /**
   * Handle the save button click to save the translations to the form
   */
  saveTranslation() {
    this.formTranslations.controls['form_elements']['controls'].forEach((element, elementIndex) => {
      if (element['controls']['form_element_values']['controls'].length) {
        element['controls']['form_element_values']['controls'].forEach((form_element_values, valueIndex) => {
          if (
            this.getFormValuesTranslations(this.formTranslations, elementIndex, valueIndex)['controls'][this.editTranslationFor.slug] &&
            !this.getFormValuesTranslations(this.form, elementIndex, valueIndex)['controls'][this.editTranslationFor.slug]
          ) {
            this.getFormValuesTranslations(this.form, elementIndex, valueIndex).addControl(
              this.editTranslationFor.slug,
              new UntypedFormControl(this.getFormValuesTranslations(this.formTranslations, elementIndex, valueIndex)['controls'][this.editTranslationFor.slug]['value'])
            )
          }
        })
      }
    })
    this.updateCurrentLanguages(this.form.controls.options.get('languages').value)
    this.form.controls.form_elements.patchValue(this.formTranslations.value.form_elements)
  }

  getFormValuesTranslations(form: UntypedFormGroup, formElementIndex: number, formValueIndex: number): UntypedFormGroup {
    return form['controls']['form_elements']['controls'][formElementIndex]['controls']['form_element_values']['controls'][formValueIndex]['controls']['options']['controls'][
      'translations'
    ]
  }

  /**
   * Get the list of thw selected languages
   * @returns string[]
   */
  currentItems(): string[] {
    return this.form.get('options').get('languages').value
  }

  /**
   * Get the list of workspace language slugs
   * @returns string[]
   */
  customLanguages(): string[] {
    return Object.keys(this.languageService.getWorkspaceLanguages())
  }

  /**
   * Deletes a language from the workspace options and updates the list of available languages
   * @param languageSlug string
   */
  deleteLanguage(languageSlug: string) {
    this.languageService
      .deleteLanguage(languageSlug)
      .pipe(takeUntil(this._destroy$))
      .subscribe((response) => {
        this.toastrService.success(response.message, '')
        this._availableLanguages = this._availableLanguages.filter((language) => language.slug !== languageSlug)
        this.updateCurrentLanguages(this.form.controls.options.get('languages').value.filter((slug) => slug !== languageSlug))
      })
  }
}
